import { useT } from '../lib/i18n';

interface Props {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Read out to assistive technology, since the switch itself has no text. */
  label: string;
  disabled?: boolean;
}

/**
 * An ON/OFF sliding switch.
 *
 * A checkbox says "is this ticked"; a switch says "is this on", which is the
 * question actually being asked in both places this is used. The state is
 * written on the track rather than only implied by the knob's position,
 * because a switch with no text is ambiguous at a glance across a room. The
 * word sits in the half the knob is not covering, so it is always on the
 * opposite side from the knob: knob right and the track green is on, knob
 * left and the track grey is off.
 *
 * Every dimension here is one fifth smaller than it was. These are settings
 * touched once when the table is set up, and at the old size two of them made
 * the top strip read as the loudest row on a board whose job is the score.
 * The proportions are unchanged — track, knob, inset and type were all scaled
 * by the same factor — so it is the same switch seen from further away.
 */
export function Toggle({ checked, onChange, label, disabled = false }: Props) {
  const t = useT();

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={[
        'relative h-[1.8rem] w-[3.6rem] shrink-0 rounded-full transition',
        'disabled:cursor-not-allowed disabled:opacity-40',
        checked ? 'bg-ball-green' : 'bg-white/15',
      ].join(' ')}
    >
      <span
        aria-hidden
        className={[
          'absolute top-1/2 -translate-y-1/2 text-[0.52rem] font-black uppercase tracking-wider',
          checked ? 'left-2 text-white' : 'right-2 text-white/60',
        ].join(' ')}
      >
        {checked ? t('toggle.on') : t('toggle.off')}
      </span>
      <span
        aria-hidden
        className={[
          'absolute top-[0.2rem] h-[1.4rem] w-[1.4rem] rounded-full bg-white shadow transition-all',
          checked ? 'left-[2rem]' : 'left-[0.2rem]',
        ].join(' ')}
      />
    </button>
  );
}
